// pages/Css.js
// Bài giảng CSS - Thiết kế Web tĩnh

import React from "react";

const styles = {
  page: {
    maxWidth: 900,
    margin: "0 auto",
    padding: "24px 20px 64px",
    color: "#1f2937",
    lineHeight: 1.65,
    fontSize: 16,
  },
  pageTitle: { fontSize: 30, fontWeight: 800, margin: "0 0 4px", color: "#111827" },
  pageSub: { color: "#6b7280", margin: "0 0 24px", fontSize: 15 },
  toc: {
    background: "#f8fafc",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "16px 20px",
    margin: "0 0 32px",
  },
  tocTitle: { fontWeight: 700, margin: "0 0 8px" },
  tocLink: { color: "#2563eb", textDecoration: "none" },
  lessonTitle: {
    fontSize: 24,
    fontWeight: 800,
    margin: "40px 0 6px",
    color: "#7c3aed",
    borderBottom: "2px solid #ede9fe",
    paddingBottom: 6,
  },
  meta: { color: "#6b7280", fontSize: 14, margin: "0 0 12px" },
  goalBox: {
    background: "#f5f3ff",
    border: "1px solid #ddd6fe",
    borderRadius: 10,
    padding: "12px 18px",
    margin: "0 0 24px",
  },
  h3: { fontSize: 19, fontWeight: 700, margin: "28px 0 8px", color: "#111827" },
  pre: {
    background: "#0f172a",
    color: "#e2e8f0",
    borderRadius: 10,
    padding: "14px 16px",
    overflowX: "auto",
    fontSize: 14,
    lineHeight: 1.5,
    margin: "10px 0 18px",
  },
  code: {
    background: "#f1f5f9",
    padding: "1px 6px",
    borderRadius: 4,
    fontSize: 14,
    color: "#be123c",
  },
  table: { borderCollapse: "collapse", width: "100%", margin: "10px 0 18px", fontSize: 15 },
  th: { border: "1px solid #d1d5db", padding: "8px 10px", background: "#f3f4f6", textAlign: "left" },
  td: { border: "1px solid #d1d5db", padding: "8px 10px" },
  note: {
    background: "#fefce8",
    border: "1px solid #fde68a",
    borderRadius: 8,
    padding: "10px 14px",
    margin: "14px 0",
    fontSize: 15,
  },
};

const Code = ({ children }) => <pre style={styles.pre}><code>{children}</code></pre>;

const practiceLinkStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  background: "#ecfdf5",
  border: "1px solid #a7f3d0",
  color: "#047857",
  textDecoration: "none",
  borderRadius: 8,
  padding: "6px 12px",
  fontSize: 14,
  fontWeight: 600,
  margin: "0 0 18px",
};
const Practice = ({ href, label }) => (
  <div>
    <a style={practiceLinkStyle} href={href} target="_blank" rel="noopener noreferrer">
      ▶ Thực hành tại W3Schools{label ? `: ${label}` : ""}
    </a>
  </div>
);
const Goal = ({ items }) => (
  <div style={styles.goalBox}>
    <strong>Mục tiêu của bài:</strong>
    <ul style={{ margin: "8px 0 0", paddingLeft: 22 }}>
      {items.map((t, i) => (
        <li key={i}>{t}</li>
      ))}
    </ul>
  </div>
);

const Css = () => (
  <div style={styles.page}>
    <h1 style={styles.pageTitle}>CSS — Trình bày trang Web</h1>
    <p style={styles.pageSub}>
      Bài giảng về CSS: tô màu, định dạng, bố cục và tạo giao diện đẹp cho trang HTML.
    </p>

    <Practice href="https://www.w3schools.com/css/" label="CSS Tutorial (trang chủ)" />
    <Practice href="https://www.w3schools.com/css/tryit.asp?filename=trycss_default" label="Try it Yourself (viết & chạy CSS ngay)" />

    <nav style={styles.toc}>
      <p style={styles.tocTitle}>Nội dung</p>
      <ul style={{ margin: 0, paddingLeft: 22 }}>
        <li><a style={styles.tocLink} href="#c1">1. CSS là gì & cách áp dụng</a></li>
        <li><a style={styles.tocLink} href="#c2">2. Cú pháp & bộ chọn (Selectors)</a></li>
        <li><a style={styles.tocLink} href="#c3">3. Màu sắc & nền (Color, Background)</a></li>
        <li><a style={styles.tocLink} href="#c4">4. Mô hình hộp (Box Model)</a></li>
        <li><a style={styles.tocLink} href="#c5">5. Văn bản & Font chữ</a></li>
        <li><a style={styles.tocLink} href="#c6">6. Hiển thị & Định vị (Display, Position)</a></li>
        <li><a style={styles.tocLink} href="#c7">7. Bố cục Flexbox</a></li>
        <li><a style={styles.tocLink} href="#c8">8. Bố cục Grid</a></li>
        <li><a style={styles.tocLink} href="#c9">9. Responsive (Media Queries)</a></li>
        <li><a style={styles.tocLink} href="#c10">10. Hiệu ứng: Transition & Animation</a></li>
      </ul>
    </nav>

    <h2 style={styles.lessonTitle}>Bài giảng CSS</h2>
    <Goal
      items={[
        "Hiểu CSS là gì và 3 cách đưa CSS vào trang;",
        "Sử dụng được các bộ chọn (selector) để áp style cho phần tử;",
        "Định dạng màu sắc, nền, văn bản, font chữ;",
        "Nắm vững mô hình hộp (box model) để căn chỉnh khoảng cách;",
        "Tạo bố cục trang bằng Flexbox và Grid;",
        "Làm trang hiển thị tốt trên nhiều kích thước màn hình (responsive);",
        "Tạo hiệu ứng chuyển động cơ bản.",
      ]}
    />

    <h3 id="c1" style={styles.h3}>1. CSS là gì & cách áp dụng</h3>
    <p>
      <strong>CSS (Cascading Style Sheets)</strong> là ngôn ngữ dùng để
      <strong> trình bày</strong> trang HTML: màu sắc, kích thước, khoảng cách, bố
      cục, font… HTML lo <em>nội dung</em>, CSS lo <em>hình thức</em>.
    </p>
    <p>Có 3 cách đưa CSS vào trang:</p>
    <Code>{`<!-- 1. Inline: viết thẳng vào thuộc tính style -->
<p style="color: red;">Chữ đỏ</p>

<!-- 2. Internal: trong thẻ <style> ở phần <head> -->
<head>
  <style>
    p { color: blue; }
  </style>
</head>

<!-- 3. External: file .css riêng (khuyên dùng) -->
<head>
  <link rel="stylesheet" href="style.css" />
</head>`}</Code>
    <div style={styles.note}>
      <strong>Nên dùng External CSS</strong> (file riêng) cho dự án thật: dễ bảo
      trì, tái sử dụng cho nhiều trang, tách bạch nội dung và giao diện.
    </div>
    <Practice href="https://www.w3schools.com/css/css_howto.asp" label="How To Add CSS" />

    <h3 id="c2" style={styles.h3}>2. Cú pháp & bộ chọn (Selectors)</h3>
    <p>Một quy tắc CSS gồm <strong>bộ chọn</strong> và khối khai báo <span style={styles.code}>thuộc-tính: giá-trị;</span></p>
    <Code>{`bộ_chọn {
  thuộc-tính: giá-trị;
}

/* Ví dụ */
h1 { color: green; font-size: 32px; }`}</Code>
    <p>Các bộ chọn thường dùng:</p>
    <table style={styles.table}>
      <thead>
        <tr><th style={styles.th}>Bộ chọn</th><th style={styles.th}>Chọn cái gì</th></tr>
      </thead>
      <tbody>
        <tr><td style={styles.td}><span style={styles.code}>p</span></td><td style={styles.td}>Tất cả thẻ <span style={styles.code}>&lt;p&gt;</span></td></tr>
        <tr><td style={styles.td}><span style={styles.code}>.tên</span></td><td style={styles.td}>Phần tử có <span style={styles.code}>class="tên"</span></td></tr>
        <tr><td style={styles.td}><span style={styles.code}>#id</span></td><td style={styles.td}>Phần tử có <span style={styles.code}>id="id"</span></td></tr>
        <tr><td style={styles.td}><span style={styles.code}>div p</span></td><td style={styles.td}><span style={styles.code}>&lt;p&gt;</span> nằm trong <span style={styles.code}>&lt;div&gt;</span></td></tr>
        <tr><td style={styles.td}><span style={styles.code}>a:hover</span></td><td style={styles.td}>Khi rê chuột vào liên kết</td></tr>
        <tr><td style={styles.td}><span style={styles.code}>*</span></td><td style={styles.td}>Mọi phần tử</td></tr>
      </tbody>
    </table>
    <Practice href="https://www.w3schools.com/css/css_selectors.asp" label="CSS Selectors" />

    <h3 id="c3" style={styles.h3}>3. Màu sắc & nền (Color, Background)</h3>
    <Code>{`p {
  color: #2563eb;                 /* màu chữ (hex) */
  background-color: rgb(240,240,240); /* màu nền (rgb) */
}

.banner {
  background-image: url("bg.jpg");
  background-size: cover;
  background-position: center;
}`}</Code>
    <p>Cách viết màu: tên (<span style={styles.code}>red</span>), hex (<span style={styles.code}>#ff0000</span>), rgb/rgba (<span style={styles.code}>rgba(255,0,0,.5)</span>), hsl.</p>
    <Practice href="https://www.w3schools.com/css/css_colors.asp" label="CSS Colors" />

    <h3 id="c4" style={styles.h3}>4. Mô hình hộp (Box Model)</h3>
    <p>
      Mỗi phần tử là một "hộp" gồm 4 lớp từ trong ra ngoài:
      <strong> content</strong> (nội dung) → <strong>padding</strong> (đệm trong) →
      <strong> border</strong> (viền) → <strong>margin</strong> (lề ngoài).
    </p>
    <Code>{`.box {
  width: 200px;
  padding: 16px;        /* khoảng đệm bên trong */
  border: 2px solid #333;
  margin: 20px;         /* khoảng cách với phần tử khác */
  box-sizing: border-box; /* tính cả padding+border vào width */
}`}</Code>
    <div style={styles.note}>
      <strong>Mẹo:</strong> đặt <span style={styles.code}>box-sizing: border-box;</span> giúp
      kích thước phần tử dễ kiểm soát hơn (width đã bao gồm padding và border).
    </div>
    <Practice href="https://www.w3schools.com/css/css_boxmodel.asp" label="CSS Box Model" />

    <h3 id="c5" style={styles.h3}>5. Văn bản & Font chữ</h3>
    <Code>{`p {
  font-family: Arial, sans-serif;
  font-size: 16px;
  font-weight: bold;
  line-height: 1.6;
  text-align: center;
  text-decoration: underline;
  letter-spacing: 1px;
}`}</Code>
    <Practice href="https://www.w3schools.com/css/css_text.asp" label="CSS Text" />

    <h3 id="c6" style={styles.h3}>6. Hiển thị & Định vị (Display, Position)</h3>
    <p>
      <span style={styles.code}>display</span>: <strong>block</strong> (chiếm cả
      dòng), <strong>inline</strong> (nằm trong dòng), <strong>inline-block</strong>,
      <strong> none</strong> (ẩn), <strong>flex</strong>, <strong>grid</strong>.
    </p>
    <Code>{`.an { display: none; }            /* ẩn phần tử */

.hop {
  position: relative;            /* mốc cho con position:absolute */
}
.huy-hieu {
  position: absolute;            /* định vị tuyệt đối theo .hop */
  top: 8px;
  right: 8px;
}

.thanh-cong-cu {
  position: sticky;              /* dính khi cuộn */
  top: 0;
}`}</Code>
    <Practice href="https://www.w3schools.com/css/css_positioning.asp" label="CSS Position" />

    <h3 id="c7" style={styles.h3}>7. Bố cục Flexbox</h3>
    <p>Flexbox giúp căn và phân bố các phần tử theo một chiều (hàng hoặc cột):</p>
    <Code>{`.container {
  display: flex;
  justify-content: space-between; /* căn theo trục ngang */
  align-items: center;            /* căn theo trục dọc */
  gap: 16px;
}
.item { flex: 1; }                /* các item chia đều */`}</Code>
    <Practice href="https://www.w3schools.com/css/css3_flexbox.asp" label="CSS Flexbox" />

    <h3 id="c8" style={styles.h3}>8. Bố cục Grid</h3>
    <p>Grid giúp tạo bố cục theo lưới 2 chiều (hàng và cột):</p>
    <Code>{`.grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr); /* 3 cột bằng nhau */
  gap: 16px;
}`}</Code>
    <Practice href="https://www.w3schools.com/css/css_grid.asp" label="CSS Grid" />

    <h3 id="c9" style={styles.h3}>9. Responsive (Media Queries)</h3>
    <p>
      Dùng <strong>media query</strong> để đổi style theo kích thước màn hình →
      trang hiển thị tốt trên cả máy tính và điện thoại.
    </p>
    <Code>{`/* Mặc định: 3 cột */
.grid { grid-template-columns: repeat(3, 1fr); }

/* Màn hình nhỏ hơn 600px: 1 cột */
@media (max-width: 600px) {
  .grid { grid-template-columns: 1fr; }
}`}</Code>
    <Practice href="https://www.w3schools.com/css/css_rwd_mediaqueries.asp" label="CSS Media Queries" />

    <h3 id="c10" style={styles.h3}>10. Hiệu ứng: Transition & Animation</h3>
    <Code>{`/* Transition: chuyển mượt khi thay đổi */
.nut {
  background: #7c3aed;
  color: #fff;
  transition: background .3s ease, transform .3s ease;
}
.nut:hover {
  background: #5b21b6;
  transform: translateY(-2px);
}

/* Animation: chuyển động lặp */
@keyframes nhayvao {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}
.the { animation: nhayvao .5s ease-out; }`}</Code>
    <Practice href="https://www.w3schools.com/css/css3_transitions.asp" label="CSS Transitions" />
    <Practice href="https://www.w3schools.com/css/css3_animations.asp" label="CSS Animations" />

    <div style={{ ...styles.note, marginTop: 32 }}>
      <strong>Thực hành:</strong> mở "Try it Yourself" của W3Schools, copy từng đoạn
      code ở trên vào và chỉnh sửa giá trị để xem kết quả thay đổi ngay lập tức.
    </div>
  </div>
);

export default Css;
